/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  CaretRightIcon,
  CoinsIcon,
  DownloadSimpleIcon,
  GooglePlayLogoIcon,
  HandCoinsIcon,
  MoneyIcon,
  PackageIcon
} from "@phosphor-icons/react";
import { CellContext, ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Button, Spinner } from "flowbite-react";
import { components, OptionProps, SingleValueProps } from "react-select";
import { BannerNote } from "../../../../components/BannerNote";
import DateRangePickerButton from "../../../../components/DateRangePicker/DateRangePickerButton";
import DynamicFilter, { DynamicFilterSelectOption, DynamicFilterValues } from "../../../../components/DynamicFilter";
import FilterSummary, { ActiveFilter } from "../../../../components/FilterSummary";
import Pagination from "../../../../components/Pagination";
import SearchInput from "../../../../components/SearchInput";
import MetricsCardsSkeleton from "../../../../components/SkeletonLoaders/MetricsCardsSkeleton";
import StatCard from "../../../../components/StatCard";
import { Table } from "../../../../components/Table";
import { CurrencyCode } from "../../../../shared/constants";
import { checkPermission, formatNumber, getCurrencySymbol } from "../../../../lib/utils";
import { useDateRange } from "../../../../contexts/DateRangeContext";
import { useAppOrdersSummary, useAuth, useIsMobile } from "../../../../hooks";
import { IDateRangePreset } from "../../../../interfaces";
import { httpService } from "../../../../services/httpService";
import {
  API_CONFIG,
  DEFAULT_ITEMS_PER_PAGE,
  LOCAL_STORAGE_KEYS,
  PERMISSION_MAPPER,
} from "../../../../shared/constants";
import COUNTRY_OPTIONS from "../../../../shared/country.json";
import AppLogo from "../../../AppCountryReports/components/AppLogo";
import AppOrdersSubTable from "../../components/AppOrdersSubTable";
import OrderDetailsModal from "../../components/OrderDetailsModal";
import { IAppSummaryItem, IOrder } from "../../interface";

interface AppOption extends DynamicFilterSelectOption {
  logo_url: string;
  platform: string;
}

const CustomAppOption = ({ children, ...props }: OptionProps<AppOption>) => {
  const app = props.data;
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <div className="min-w-8 w-8 min-h-8 h-8 flex justify-center items-center border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
          <AppLogo
            logoUrl={app?.logo_url || null}
            platform={app?.platform || ""}
            appName={props.data.label}
          />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{children}</span>
          <span className="text-xs text-gray-500">{app.platform as string}</span>
        </div>
      </div>
    </components.Option>
  );
};

const SingleAppValueWithIcon = ({ children, ...props }: SingleValueProps<AppOption>) => {
  const app = props.data as AppOption;
  return (
    <components.SingleValue {...props} key={app?.value}>
      <div className="flex items-center gap-2">
        <div className="min-w-6 w-6 min-h-6 h-6 flex justify-center items-center border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
          <AppLogo
            logoUrl={app?.logo_url || null}
            platform={app?.platform || ""}
            appName={props.data.label}
          />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{children}</span>
        </div>
      </div>
    </components.SingleValue>
  );
};

const CustomCountryOption = ({ children, ...props }: OptionProps<DynamicFilterSelectOption>) => {
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        {props.data.value !== "all" && (
          <img
            src={`/images/svg_flags/${props.data.value}.svg`}
            alt={props.data.label}
            className="w-6 h-4 rounded-sm border"
            crossOrigin="anonymous"
          />
        )}
        <span>{children}</span>
      </div>
    </components.Option>
  );
};

const SingleCountryValueWithFlag = ({ children, ...props }: SingleValueProps<DynamicFilterSelectOption>) => (
  <components.SingleValue {...props}>
    <div className="flex items-center gap-2">
      {props.data.value !== "all" && (
        <img
          src={`/images/svg_flags/${props.data.value}.svg`}
          alt={props.data.label}
          className="w-6 h-4 rounded-sm border"
          crossOrigin="anonymous"
        />
      )}
      <span>{children}</span>
    </div>
  </components.SingleValue>
);

const StatusOption = ({ children, ...props }: OptionProps<any>) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full bg-${data.color}-400`} />
        <span>{children}</span>
      </div>
    </components.Option>
  );
};

const StatusSingleValue = ({ children, ...props }: SingleValueProps<any>) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full bg-${data.color}-400`} />
        <span>{children}</span>
      </div>
    </components.SingleValue>
  );
};


const OrdersList: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { selectedIntegration } = useAuth();
  const [currency] = useState<CurrencyCode>("USD");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<{ label: string; value: string } | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<{ label: string; value: string } | null>(null);
  const [selectedApp, setSelectedApp] = useState<AppOption | null>(null);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);

  const { dateRange, selectedPreset, setDateRange, setSelectedPreset } =
    useDateRange();

  const [searchParams, setSearchParams] = useSearchParams();

  // URL State Management
  const urlApp = searchParams.get("app");
  const urlCountry = searchParams.get("country");
  const urlStatus = searchParams.get("status");
  const urlPage = searchParams.get("page");
  const urlPerPage = searchParams.get("per_page");
  const urlSearch = searchParams.get("search");

  // Sync state from URL on mount
  React.useEffect(() => {
    if (urlPage) setCurrentPage(Number(urlPage));
    if (urlPerPage) setItemsPerPage(Number(urlPerPage));
    if (urlSearch) setSearchQuery(urlSearch);
  }, [urlPage, urlPerPage, urlSearch]);

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);

    try {
      const fromDate = dateRange.startDate ? dayjs(dateRange.startDate).format("YYYY-MM-DD") : undefined;
      const toDate = dateRange.endDate ? dayjs(dateRange.endDate).format("YYYY-MM-DD") : undefined;

      const params: any = {
        tracking_id: selectedIntegration?.value,
        start_date: fromDate,
        end_date: toDate,
        search: searchQuery,
        country: selectedCountry?.value,
        status: selectedStatus?.value,
        app_slug: selectedApp?.value,
        export: 'excel',
      };

      const response = await httpService.get<Blob>(API_CONFIG.path.googleIapAppOrdersConsolidated, params, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders_export_${dayjs().format("YYYY-MM-DD")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // useEffect(() => {
  //   const startOfLastMonth = dayjs().subtract(1, "month").startOf("month").toDate();
  //   const endOfLastMonth = dayjs().subtract(1, "month").endOf("month").toDate();

  //   setDateRange(startOfLastMonth, endOfLastMonth);
  //   setSelectedPreset(undefined);
  // }, []);

  const { data: summaryData, isLoading } = useAppOrdersSummary({
    startDate: dateRange.startDate || undefined,
    endDate: dateRange.endDate || undefined,
    page: currentPage,
    per_page: itemsPerPage,
    search: searchQuery,
    country: selectedCountry?.value ?? undefined,
    status: selectedStatus?.value ?? undefined,
    app_slug: selectedApp?.value ?? undefined,
    sort_by: sortBy,
    sort_order: sortOrder as any,
  });

  const appSummaries = useMemo(
    () => summaryData?.results || [],
    [summaryData]
  );

  const metrics = useMemo(
    () => summaryData?.metrics,
    [summaryData]
  );

  // Expand first row by default when data loads
  // React.useEffect(() => {
  //   if (appSummaries.length > 0 && Object.keys(expandedRows).length === 0) {
  //     setExpandedRows({ "0": true });
  //   }
  // }, [appSummaries, expandedRows]);

  // Filter options - these should ideally come from an API if they are dynamic, 
  // but for now we keep the existing or adapt to the new structure.
  // The user mentioned country and status filters should be server-side.

  const statusOptions = useMemo(() => [
    { label: "Charged", value: "1", color: "green" },
    { label: "Refund", value: "2", color: "red" },
    { label: "Partial Refund", value: "3", color: "yellow" },
    { label: "Chargeback", value: "4", color: "gray" },
  ], [t]);

  // Synchronize URL with local state
  const updateUrlParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value && value !== "all") {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Hydrate initial states from URL/Data
  React.useEffect(() => {
    if (appSummaries.length > 0 && urlApp && !selectedApp) {
      const app = appSummaries.find(a => a.app_slug === urlApp);
      if (app) setSelectedApp({
        label: app.app_name,
        value: app.app_slug,
        logo_url: (app as any).app_logo,
        platform: (app as any).app_platform
      });
    }
  }, [appSummaries, urlApp, selectedApp]);

  React.useEffect(() => {
    if (urlCountry && !selectedCountry) {
      const country = COUNTRY_OPTIONS.find(c => c.value === urlCountry);
      if (country) setSelectedCountry(country);
    }
  }, [urlCountry, selectedCountry, COUNTRY_OPTIONS]);

  React.useEffect(() => {
    if (urlStatus && !selectedStatus) {
      const status = statusOptions.find(s => s.value === urlStatus);
      if (status) setSelectedStatus(status);
    }
  }, [urlStatus, selectedStatus, statusOptions]);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
    updateUrlParams({ search: value || null, page: "1" });
  }, [updateUrlParams]);

  const handleApplyFilters = (values: DynamicFilterValues) => {
    const country = values.country as { label: string; value: string } | null;
    const status = values.status as { label: string; value: string } | null;
    const app = values.app as { label: string; value: string } | null;

    setSelectedCountry(country);
    setSelectedStatus(status);
    setSelectedApp(app as AppOption | null);
    setCurrentPage(1);

    updateUrlParams({
      app: app?.value as string || null,
      country: country?.value as string || null,
      status: status?.value as string || null,
      page: "1"
    });
  };

  const activeFilters = useMemo(() => {
    const filters: ActiveFilter[] = [];
    if (selectedApp) {
      filters.push({
        id: "app",
        label: selectedApp.label,
        icon: (
          <div className="bg-white rounded p-0.5 border border-gray-200 shrink-0 h-6 w-6 flex items-center justify-center">
            <AppLogo
              logoUrl={selectedApp.logo_url}
              platform={selectedApp.platform}
              appName={selectedApp.label}
            />
          </div>
        ),
        onClear: () => {
          setSelectedApp(null);
          updateUrlParams({ app: null, page: "1" });
        },
      });
    }
    if (selectedCountry && selectedCountry.value !== 'all') {
      filters.push({
        id: "country",
        label: selectedCountry.label,
        icon: (
          <img
            src={`/images/svg_flags/${selectedCountry.value}.svg`}
            alt={selectedCountry.label}
            className="w-4 h-3 rounded-sm shrink-0"
          />
        ),
        onClear: () => {
          setSelectedCountry(null);
          updateUrlParams({ country: null, page: "1" });
        },
      });
    }
    if (selectedStatus && selectedStatus.value !== 'all') {
      filters.push({
        id: "status",
        label: selectedStatus.label,
        icon: (
          <div className={`w-1.5 h-1.5 rounded-full bg-${(selectedStatus as any).color}-400 shrink-0`} />
        ),
        onClear: () => {
          setSelectedStatus(null);
          updateUrlParams({ status: null, page: "1" });
        },
      });
    }
    return filters;
  }, [selectedApp, selectedCountry, selectedStatus, updateUrlParams]);

  const handleClearFilters = () => {
    setSelectedCountry(null);
    setSelectedStatus(null);
    setSelectedApp(null);
    setCurrentPage(1);
    updateUrlParams({ app: null, country: null, status: null, page: "1" });
  };

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DATA_RANGE_FROM,
      startDate.toISOString()
    );
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DATA_RANGE_TO,
      endDate.toISOString()
    );
    setDateRange(startDate, endDate);
    setCurrentPage(1);
  };

  const handlePresetChange = (preset: IDateRangePreset | undefined) => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.DATA_RANGE_PRESET,
      preset?.id || ""
    );
    setSelectedPreset(preset);
  };

  // Check permissions
  const hasOrderExportPermission = useMemo(
    () =>
      checkPermission(
        PERMISSION_MAPPER.ORDER_EXPORT,
        "modules",
        user?.user_permissions
      ),
    [user?.user_permissions]
  );

  const columns = useMemo<ColumnDef<IAppSummaryItem>[]>(() => {
    return [
      {
        id: "expand",
        header: "",
        size: 10,
        minSize: 10,
        cell: (props: any) => {
          const { isExpanded } = props as any;
          return <div className="flex justify-center items-center">
            <CaretRightIcon size={16} weight="bold" className={`transition-transform duration-300 ${isExpanded ? "rotate-90" : ""}`} />
          </div>
        },
        accessorFn: () => null,
      } as ColumnDef<IAppSummaryItem, any>,
      {
        id: "app_info",
        accessorKey: "app_name",
        header: t("orders.columns.appInfo"),
        size: 250,
        cell: ({ row }: CellContext<IAppSummaryItem, any>) => {
          const app = row.original as any;
          return (
            <div className="flex items-center gap-3">
              <img src={app.app_logo} alt={app.app_name} className="w-10 h-10 rounded-lg bg-gray-100 object-contain p-1" />
              <div className="flex flex-col">
                <span className="text-sm font-bold text-gray-800 line-clamp-1">{app.app_name}</span>
                <span className="text-[10px] text-gray-500 font-medium">
                  {app.package_name}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        id: "gross",
        accessorKey: "total_revenue_usd",
        header: t("orders.columns.totalGross"),
        size: 140,
        cell: ({ row }: CellContext<IAppSummaryItem, any>) => {
          const val = currency === "INR" ? row.original.metrics.total_revenue.inr : row.original.metrics.total_revenue.usd;
          return (
            <div className="flex flex-col">
              <span className="text-sm font-bold text-gray-800">
                {getCurrencySymbol(currency as CurrencyCode)}{formatNumber(val)}
              </span>
            </div>
          );
        },
      },
      {
        id: "tax",
        accessorKey: "metrics.total_tax.usd",
        header: t("orders.columns.tax"),
        size: 120,
        cell: ({ row }: CellContext<IAppSummaryItem, any>) => {
          // Assuming tax follows same currency logic or is provided in both
          const val = row.original.metrics.total_tax.usd;
          return (
            <div className="flex flex-col">
              <span className="text-sm font-bold text-yellow-500">
                ${formatNumber(val)}
              </span>
            </div>
          );
        },
      },
      {
        id: "total_orders",
        accessorKey: "metrics.total_orders",
        header: t("orders.columns.totalOrders"),
        size: 120,
        cell: ({ row }: CellContext<IAppSummaryItem, any>) => (
          <span className="text-sm font-bold text-gray-800">
            {row.original.metrics.total_orders}
          </span>
        ),
      },
    ];
  }, [t, currency]);

  return (
    <div className="flex flex-col gap-2" >
      <div className="flex flex-col gap-1">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-2">
          <div>
            <div className="flex items-center gap-2">
              <GooglePlayLogoIcon size={20} className="text-gray-700" weight="duotone" />
              <h1 className="text-md font-bold text-gray-700">
                {t("orders.title")}
              </h1>
            </div>
            {isLoading ? (
              <div className="w-[200px] h-4 rounded bg-gray-200 animate-pulse mt-1"></div>
            ) : (
              <>
                {summaryData && (
                  <div className="text-xs text-gray-600 mt-1">
                    {`${t("common.total")}: ${summaryData.total} ${t("orders.items")}`}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-wrap">
            <div className="flex flex-row gap-2">
              {hasOrderExportPermission && (
                <Button
                  color="light"
                  onClick={handleExport}
                  disabled={!appSummaries.length}
                  isProcessing={isExporting}
                  className="flex-1 sm:flex-none"
                  processingSpinner={
                    <Spinner size="sm" color="gray" className="-mt-[2px]" />
                  }
                  size="sm"
                >
                  <div className="flex items-center gap-2">
                    {!isExporting && <DownloadSimpleIcon size={18} weight="bold" className={isExporting ? "animate-spin" : ""} />}
                    {isExporting ? t("common.exporting") : t("common.export")}
                  </div>
                </Button>
              )}
              <div className="flex-1 sm:flex-none">
                <DynamicFilter
                  title={t("common.filter")}
                  onApply={handleApplyFilters}
                  fields={[
                    {
                      id: "app",
                      label: t("orders.filters.app"),
                      type: "select",
                      options: appSummaries.map(app => ({
                        label: app.app_name,
                        value: app.app_slug,
                        logo_url: app.app_logo,
                        platform: app.app_platform
                      })),
                      value: selectedApp,
                      selectComponents: {
                        Option: CustomAppOption,
                        SingleValue: SingleAppValueWithIcon,
                      },
                    },
                    {
                      id: "country",
                      label: t("orders.filters.country"),
                      type: "select",
                      options: COUNTRY_OPTIONS,
                      value: selectedCountry,
                      selectComponents: {
                        Option: CustomCountryOption,
                        SingleValue: SingleCountryValueWithFlag,
                      },
                    },
                    {
                      id: "status",
                      label: t("orders.filters.status"),
                      type: "select",
                      options: statusOptions,
                      value: selectedStatus,
                      selectComponents: {
                        Option: StatusOption,
                        SingleValue: StatusSingleValue,
                      },
                    },
                  ]}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <DateRangePickerButton
                onDateRangeChange={handleDateRangeChange}
                onPresetChange={handlePresetChange}
                initialStartDate={dateRange.startDate}
                initialEndDate={dateRange.endDate}
                initialPreset={selectedPreset?.id}
              />
              <SearchInput
                onSearch={handleSearch}
                placeholder={t("common.search")}
                className="w-full sm:w-[250px]"
                style={{ height: "38px" }}
                size="md"
              />
            </div>
          </div>
        </div>

        {/* Filter Summary Tags */}
        <FilterSummary
          filters={activeFilters}
          onClearAll={handleClearFilters}
          title={t("common.appliedFilters")}
          clearAllLabel={t("common.resetAllFilters") || "Clear All"}
        />
      </div>

      {isLoading ? (
        <MetricsCardsSkeleton count={4} />
      ) : metrics ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title={t("orders.metrics.grossVolume")}
            value={currency === "INR" ? metrics.total_revenue.inr : metrics.total_revenue.usd}
            isCurrency={true}
            currency={currency === "INR" ? "INR" : "USD"}
            icon={
              <MoneyIcon size={20} className="text-gray-700" weight="duotone" />
            }
          />
          <StatCard
            title={t("orders.metrics.taxLiability")}
            value={currency === "INR" ? metrics.total_tax.inr : metrics.total_tax.usd}
            isCurrency={true}
            currency={currency === "INR" ? "INR" : "USD"}
            icon={
              <HandCoinsIcon
                size={20}
                className="text-gray-700"
                weight="duotone"
              />
            }
          />
          <StatCard
            title={t("orders.metrics.estimatedProceeds")}
            value={currency === "INR" ? metrics.total_developer_revenue.inr : metrics.total_developer_revenue.usd}
            isCurrency={true}
            currency={currency === "INR" ? "INR" : "USD"}
            icon={
              <CoinsIcon size={20} className="text-gray-700" weight="duotone" />
            }
          />
          <StatCard
            title={t("orders.metrics.orderCount")}
            value={metrics.total_orders}
            icon={
              <PackageIcon
                size={20}
                className="text-gray-700"
                weight="duotone"
              />
            }
          />
        </div>
      ) : null}

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table
            data={appSummaries}
            columns={columns}
            isLoading={isLoading}
            showPagination={false}
            showSearch={false}
            loadingHeight={3.8}
            loadingRowCount={10}
            pageSize={20}
            maxHeight={'h-full'}
            isMobile={isMobile}
            summaryKeys={[
              { key: 'app_info', customValue: "SUMMARY", align: "left", className: "text-xs font-bold text-gray-800" },
              { key: 'gross', value: currency === "INR" ? "metrics.total_revenue.inr" : "metrics.total_revenue.usd", align: "left", prepend: getCurrencySymbol(currency), className: "text-sm font-bold text-gray-800" },
              { key: 'tax', value: 'metrics.total_tax.usd', align: "left", prepend: getCurrencySymbol(currency), className: "text-sm font-bold text-gray-800" },
              { key: 'total_orders', value: 'metrics.total_orders', align: "left", precision: 0, className: "text-sm font-bold text-gray-800" },
            ]}
            onSort={(sorting) => {
              if (sorting.length > 0) {
                setSortBy(sorting[0].id);
                setSortOrder(sorting[0].desc ? "desc" : "asc");
              } else {
                setSortBy(undefined);
                setSortOrder(undefined);
              }
            }}
            renderRowDetails={(app) => (
              <AppOrdersSubTable
                appSlug={app.app_slug}
                country={selectedCountry?.value}
                status={selectedStatus?.value}
                search={searchQuery}
                loadingRowCount={app.metrics?.total_orders || 0}
                orders={app.orders}
                onOrderClick={(order: IOrder) => {
                  setSelectedOrder(order);
                  setShowOrderModal(true);
                }}
              />
            )}
          />
        </div>

        {!isLoading && appSummaries.length > 0 && (
          <div className="px-4 pt-4 pb-2 border-t border-gray-100 flex justify-end">
            <Pagination
              currentPage={currentPage}
              totalPages={summaryData?.last_page || 1}
              totalItems={summaryData?.total || 0}
              itemsPerPage={itemsPerPage}
              onPageChange={(page: number) => {
                setCurrentPage(page);
                updateUrlParams({ page: String(page) });
              }}
              onItemsPerPageChange={(val: number) => {
                setItemsPerPage(val);
                setCurrentPage(1); // Reset to first page when per_page changes
                updateUrlParams({ per_page: String(val), page: "1" });
              }}
              showItemsPerPage={true}
            />
          </div>
        )}

        <BannerNote
          note={t("common.googleDataNote")}
          title={t("common.dataSourceInfo")}
          marqueeSpeed={25}
          delay={3}
          rootClassName="mt-2"
        />
      </div>

      <OrderDetailsModal
        show={showOrderModal && !!selectedOrder}
        onClose={() => {
          setShowOrderModal(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
      />
    </div>
  );
};

export default OrdersList;
